import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FaGithub, FaExternalLinkAlt, FaReact, FaNodeJs, FaDatabase } from 'react-icons/fa';
import { SiMongodb, SiFirebase, SiTailwindcss, SiLaravel, SiPhp, SiMysql, SiBootstrap } from 'react-icons/si';
import './Projects.css';

const Projects = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const [activeFilter, setActiveFilter] = useState('all');

  // Projects array - Update 'live' field with your subdomain URLs
  // Format: https://subdomain.akashboda.tech
  const projects = [
    {
      id: 1,
      title: "E-Commerce Platform",
      description: "A full-stack e-commerce platform built with Laravel featuring user authentication, product management, shopping cart, wishlist, and secure checkout. Includes admin dashboard, order management, and customer support features.",
      image: "https://akashboda.tech/images/ecom-laravel.png",
      technologies: ["Laravel", "PHP", "MySQL", "Bootstrap"],
      category: "fullstack",
      github: "https://github.com",
      live: "https://ecom-laravel.akashboda.tech",
      featured: true
    },
    {
      id: 2,
      title: "Trimurti Plastic & Packaging",
      description: "A comprehensive business website for Trimurti Plastic & Packaging company. Features product catalog, company information, contact management, and modern responsive design for showcasing plastic and packaging solutions.",
      image: "https://akashboda.tech/images/tpp-box-strap.png",
      technologies: ["Laravel", "PHP", "MySQL"],
      category: "fullstack",
      github: "https://github.com",
      live: "https://trimurtiplastic.com",
      featured: true
    },
    {
      id: 3,
      title: "Inventory Pro",
      description: "A powerful inventory management system for tracking products, stock levels, and financial transactions. Features include real-time inventory tracking, money flow management, sales reports, purchase orders, low stock alerts, and comprehensive analytics dashboard.",
      image: "https://akashboda.tech/images/Inventory-pro.png",
      technologies: ["Laravel", "PHP", "MySQL", "Bootstrap"],
      category: "fullstack",
      github: "https://github.com",
      live: "https://invetorypro.akashboda.tech/",
      featured: true
    },
    
  ];

  const filters = [
    { name: 'All', value: 'all' },
    { name: 'Frontend', value: 'frontend' },
    { name: 'Backend', value: 'backend' },
    { name: 'Full Stack', value: 'fullstack' }
  ];

  const filteredProjects = activeFilter === 'all' 
    ? projects 
    : projects.filter(project => project.category === activeFilter);

  const getTechnologyIcon = (tech) => {
    const iconMap = {
      'React': <FaReact />,
      'Node.js': <FaNodeJs />,
      'MongoDB': <SiMongodb />,
      'Firebase': <SiFirebase />,
      'Tailwind CSS': <SiTailwindcss />,
      'Laravel': <SiLaravel />,
      'PHP': <SiPhp />,
      'MySQL': <SiMysql />,
      'Bootstrap': <SiBootstrap />,
      'Database': <FaDatabase />
    };
    return iconMap[tech] || <FaReact />;
  };

  return (
    <section id="projects" className="projects">
      <div className="container">
        <motion.div 
          className="section-header"
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <h2>My Projects</h2>
          <p className="section-subtitle">
            Explore my portfolio of innovative projects and solutions. Each project demonstrates my expertise in modern web development and problem-solving skills.
          </p>
        </motion.div>

        <motion.div 
          className="project-filters"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          {filters.map((filter) => (
            <button
              key={filter.value}
              className={`filter-btn ${activeFilter === filter.value ? 'active' : ''}`}
              onClick={() => setActiveFilter(filter.value)}
            >
              {filter.name}
            </button>
          ))}
        </motion.div>

        <div className="projects-grid" ref={ref}>
          {filteredProjects.map((project, index) => (
            <motion.div 
              key={project.id}
              className={`project-card ${project.featured ? 'featured' : ''}`}
              initial={{ opacity: 0, y: 50 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              whileHover={{ y: -10 }}
            >
              <div className="project-image">
                <img src={project.image} alt={project.title} />
                <div className="project-overlay">
                  <div className="project-links">
                    <a href={project.github} target="_blank" rel="noopener noreferrer" className="project-link">
                      <FaGithub />
                    </a>
                    <a href={project.live} target="_blank" rel="noopener noreferrer" className="project-link">
                      <FaExternalLinkAlt />
                    </a>
                  </div>
                </div>
                {project.featured && (
                  <div className="featured-badge">
                    Featured
                  </div>
                )}
              </div>
              
              <div className="project-content">
                <h3>{project.title}</h3>
                <p>{project.description}</p>
                
                <div className="project-technologies">
                  {project.technologies.map((tech) => (
                    <span key={tech} className="tech-tag">
                      {getTechnologyIcon(tech)}
                      {tech}
                    </span>
                  ))}
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div 
          className="view-more"
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : {}}
          transition={{ duration: 0.6, delay: 0.8 }}
        >
          <a href="https://github.com" target="_blank" rel="noopener noreferrer" className="btn btn-secondary">
            View More on GitHub
          </a>
          <a href="https://akashboda.tech" target="_blank" rel="noopener noreferrer" className="btn btn-primary" style={{ marginLeft: '1rem' }}>
            Visit My Website
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default Projects;
